import ViewOffFilled16 from '@carbon/icons-react/es/view--off--filled/16';
import { SecureRoute } from '@okta/okta-react';
import { Space, Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, Switch, useParams, useRouteMatch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { ProjectId, SpaceTabId } from 'api/models';
import { IconShell } from 'components/atoms/icon-shell';
import { PageLoading } from 'components/atoms/loading';
import { NotFound as NotFoundAtom } from 'components/atoms/result';
import { SpaceIcon20 } from 'components/atoms/space-icon';
import { Page } from 'components/molecules/page';
import {
  Captions,
  Header as PageHeader,
} from 'components/molecules/page/header';
import { CurrentSpaceContext } from 'components/organisms/spaces/context';
import { Caption01, Text02 } from 'components/styles';
import { useProjectSpaces, useSpace } from 'data/spaces/hooks';
import { useSpaceTabHistoryLocalStorageState } from 'hooks/use-space-tab-local-storage-state';
import { useTranslation } from 'hooks/use-translation';
import { useProjectWebsocket } from 'hooks/websockets';
import { getProjectPlanUrl, getSpaceTabDetailUrl } from 'utils/urls';
import { SpaceTabsPage } from './space-tabs';

interface Params {
  projectId: string;
  spaceId: string;
}

const Header = styled(PageHeader)<{ isPrivate?: boolean }>`
  && {
    box-shadow: none;
  }
  /* This captions div and we need to remove padding left because we are using icon in title and we want to alight it */
  && > div {
    padding-left: var(--spacing-03);
  }
  ${ifProp(
    'isPrivate',
    css`
      && {
        padding-top: var(--spacing-03);
      }

      ${Captions} {
        padding-top: 0;
        padding-bottom: 0;
      }
    `,
  )};
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  width: 400px;
  justify-content: space-between;
  padding-left: var(--layout-01);
  height: 40px;
`;

const HeaderTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  > svg {
    margin-right: var(--spacing-03);
  }
`;

const Icon = styled(IconShell)`
  margin-right: var(--spacing-03);
`;

const PrivateIcon = styled(ViewOffFilled16)`
  color: var(--yellow-50);
`;

const HeaderSubTitle = styled.div`
  ${Caption01};
  ${Text02};
`;

const FlexBox = styled.div`
  display: flex;
`;

const NotFound = styled(NotFoundAtom)`
  padding-top: var(--spacing-12);
`;

const HeaderTitle = ({
  title,
  isPrivate,
}: {
  title: string;
  isPrivate: boolean;
}) => (
  <HeaderTitleContainer>
    {isPrivate && (
      <Tooltip
        title={<FormattedMessage id="projects.space.private.description" />}
        placement="rightBottom"
      >
        <Space size={4}>
          <IconShell size="small" icon={<PrivateIcon />} />
          <HeaderSubTitle>
            <FormattedMessage id="projects.space.private" />
          </HeaderSubTitle>
        </Space>
      </Tooltip>
    )}
    <FlexBox>
      <Icon size="small" icon={<SpaceIcon20 name={title} />} />
      {title}
    </FlexBox>
  </HeaderTitleContainer>
);

const RedirectToSpaceTabsPage: FC = () => {
  const params = useParams<Params>();
  const projectId = Number(params.projectId);
  const spaceId = Number(params.spaceId);
  const { data, isLoading } = useSpace(spaceId);
  const { value: recentlyVisitedSpace } = useSpaceTabHistoryLocalStorageState();

  if (isLoading) {
    return <Loading />;
  }
  let spaceTabIdFromLocal;

  if (recentlyVisitedSpace) {
    spaceTabIdFromLocal = recentlyVisitedSpace[spaceId];
  }

  let currentSpaceTabId: 'new' | number = 'new';

  if (data) {
    const { spaceTabs } = data;

    if (
      spaceTabIdFromLocal &&
      spaceTabs.find((d) => {
        return d === spaceTabIdFromLocal;
      })
    ) {
      currentSpaceTabId = spaceTabIdFromLocal;
    } else if (spaceTabs.length) {
      currentSpaceTabId = Number(spaceTabs[0].spaceTabId);
    }
  }

  return (
    <Redirect
      to={{
        pathname: getSpaceTabDetailUrl(
          SpaceTabId(projectId, spaceId, currentSpaceTabId),
        ),
      }}
    />
  );
};

export const SpacesDetailPage: FC = () => {
  const params = useParams<Params>();
  const { path } = useRouteMatch();
  const spaceId = Number(params.spaceId);
  const projectId = Number(params.projectId);
  const { data, isLoading, isError } = useSpace(spaceId);
  const { isLoading: isSpacesLoading } = useProjectSpaces(ProjectId(projectId));
  const actionText = useTranslation('space.404.action');

  useProjectWebsocket(ProjectId(projectId));

  if (isLoading || isSpacesLoading) {
    return <PageLoading />;
  }

  return data ? (
    <>
      <Header
        isPrivate={data.private}
        compact
        title={
          <Page.Header.Title>
            <HeaderTitle title={data.spaceName} isPrivate={data.private} />
          </Page.Header.Title>
        }
      />
      <Page.Content>
        <CurrentSpaceContext.Provider value={data}>
          <Switch>
            <SecureRoute exact path={`${path}/tabs/:spaceTabId`}>
              <SpaceTabsPage />
            </SecureRoute>
            <SecureRoute exact path={path}>
              <RedirectToSpaceTabsPage />
            </SecureRoute>
          </Switch>
        </CurrentSpaceContext.Provider>
      </Page.Content>
    </>
  ) : isError ? (
    <NotFound
      actionText={actionText}
      linkTo={getProjectPlanUrl(ProjectId(projectId))}
    />
  ) : (
    <Redirect
      to={{
        pathname: getProjectPlanUrl(ProjectId(projectId)),
      }}
    />
  );
};
